import {isNumber} from 'lodash';

import {ClientDevice} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {UserSettings} from 'types';
import {settingsUtils} from 'utils';
import {formatSeconds} from 'utils/transform';

import {ActivityMetric, ActivityMetricColor, ActivityMetricDefaultColor} from './enums';
import {ActivityMetricConfig} from './types';

const {STATISTICS} = TRANSLATION_KEYS.USER.ACTIVITY;
const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;

export const METRIC_CONFIG = {
    AVG_DURATION: {
        metric: ActivityMetric.AvgDuration,
        color: ActivityMetricColor[ActivityMetric.AvgDuration],
        title: STATISTICS.AVG_DURATION.TITLE,
        tooltip: STATISTICS.AVG_DURATION.TOOLTIP,
        valueGetter: (s) => s.avgDuration,
        valueFormatter: (value) => isNumber(value) ? formatSeconds(value) : null
    } as ActivityMetricConfig,
    AVG_RESPONSE_TIME: {
        metric: ActivityMetric.AvgResponseTime,
        color: ActivityMetricColor[ActivityMetric.AvgResponseTime],
        title: STATISTICS.AVG_RESPONSE_TIME.TITLE,
        tooltip: STATISTICS.AVG_RESPONSE_TIME.TOOLTIP,
        valueGetter: (s) => s.avgResponseTime,
        valueFormatter: (value) => isNumber(value) ? formatSeconds(value) : null
    } as ActivityMetricConfig,
    TOTAL: {
        metric: ActivityMetric.Total,
        color: ActivityMetricColor[ActivityMetric.Total],
        title: STATISTICS.TOTAL.TITLE,
        tooltip: STATISTICS.TOTAL.TOOLTIP,
        valueGetter: (s) => s.total,
    } as ActivityMetricConfig,
    UNIQUE_VISITORS: {
        metric: ActivityMetric.UniqueVisitors,
        color: ActivityMetricColor[ActivityMetric.UniqueVisitors],
        title: STATISTICS.UNIQUE_VISITORS.TITLE,
        tooltip: STATISTICS.UNIQUE_VISITORS.TOOLTIP,
        valueGetter: (s) => s.uniqueClients
    } as ActivityMetricConfig,
    MISSED: {
        metric: ActivityMetric.Missed,
        color: ActivityMetricColor[ActivityMetric.Missed],
        ratioColor: ActivityMetricDefaultColor,
        title: STATISTICS.MISSED.TITLE,
        tooltip: STATISTICS.MISSED.TOOLTIP,
        valueGetter: (s) => s.missed,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
    ANSWERED: {
        metric: ActivityMetric.Answered,
        color: ActivityMetricColor[ActivityMetric.Answered],
        title: STATISTICS.ANSWERED.TITLE,
        tooltip: STATISTICS.ANSWERED.TOOLTIP,
        valueGetter: (s) => s.answered,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
    SUCCESSFUL: {
        metric: ActivityMetric.Successful,
        color: ActivityMetricColor[ActivityMetric.Successful],
        title: STATISTICS.SUCCESSFUL.TITLE,
        tooltip: STATISTICS.SUCCESSFUL.TOOLTIP,
        valueGetter: (s) => s.successful,
        totalGetter: (s) => s.answered
    } as ActivityMetricConfig,
    FAILED: {
        metric: ActivityMetric.Failed,
        color: ActivityMetricColor[ActivityMetric.Failed],
        title: STATISTICS.FAILED.TITLE,
        tooltip: STATISTICS.FAILED.TOOLTIP,
        valueGetter: (s) => s.failed,
        totalGetter: (s) => s.answered
    } as ActivityMetricConfig,
    NO_RESULT: {
        metric: ActivityMetric.NoResult,
        color: ActivityMetricDefaultColor,
        ratioColor: ActivityMetricDefaultColor,
        title: STATISTICS.NO_RESULT.TITLE,
        tooltip: STATISTICS.NO_RESULT.TOOLTIP,
        valueGetter: (s) => s.answered - s.successful - s.failed,
        totalGetter: (s) => s.answered
    } as ActivityMetricConfig,
    PRE_SESSION_FLOW: {
        metric: ActivityMetric.SessionFlowTotal,
        color: ActivityMetricColor[ActivityMetric.SessionFlowTotal],
        title: STATISTICS.PRE_SESSION_FLOW.TITLE,
        tooltip: STATISTICS.PRE_SESSION_FLOW.TOOLTIP,
        valueGetter: (s) => s.sessionFlow?.preSessionFlow || 0,
        totalGetter: (s) => s.sessionFlow?.preSessionFlow || 0
    },
    ESCALATION_ANSWERED: {
        metric: ActivityMetric.EscalationAnswered,
        color: ActivityMetricColor[ActivityMetric.EscalationAnswered],
        title: STATISTICS.ESCALATION_ANSWERED.TITLE,
        tooltip: STATISTICS.ESCALATION_ANSWERED.TOOLTIP,
        valueGetter: (s) => s.sessionFlow?.answered || 0,
        totalGetter: (s) => s.sessionFlow?.preSessionFlow || 0
    },
    ESCALATION_MISSED: {
        metric: ActivityMetric.EscalationMissed,
        color: ActivityMetricColor[ActivityMetric.EscalationMissed],
        title: STATISTICS.ESCALATION_MISSED.TITLE,
        tooltip: STATISTICS.ESCALATION_MISSED.TOOLTIP,
        valueGetter: (s) => s.sessionFlow?.missed || 0,
        totalGetter: (s) => s.sessionFlow?.preSessionFlow || 0
    },
    SELF_RESOLVED: {
        metric: ActivityMetric.SelfResolved,
        color: ActivityMetricColor[ActivityMetric.SelfResolved],
        title: STATISTICS.SELF_RESOLVED.TITLE,
        tooltip: STATISTICS.SELF_RESOLVED.TOOLTIP,
        valueGetter: (s) => s.sessionFlow?.selfResolved || 0,
        totalGetter: (s) => s.sessionFlow?.preSessionFlow || 0
    },
    ALL_FILES_PROCESSED: {
        metric: ActivityMetric.AllFilesProcessed,
        color: ActivityMetricColor[ActivityMetric.AllFilesProcessed],
        title: STATISTICS.ALL_FILES_PROCESSED.TITLE,
        tooltip: STATISTICS.ALL_FILES_PROCESSED.TOOLTIP,
        valueGetter: (s) => s.allFilesProcessed,
        totalGetter: (s) => s.answered,
        isHidden: ({settings}: {settings: UserSettings}) => !settingsUtils.canSeeFilePipeline(settings)
    } as ActivityMetricConfig,
    SOME_FILES_UNPROCESSED: {
        metric: ActivityMetric.SomeFilesUnprocessed,
        color: ActivityMetricColor[ActivityMetric.SomeFilesUnprocessed],
        title: STATISTICS.SOME_FILES_UNPROCESSED.TITLE,
        tooltip: STATISTICS.SOME_FILES_UNPROCESSED.TOOLTIP,
        valueGetter: (s) => s.someFilesUnprocessed,
        totalGetter: (s) => s.answered,
        isHidden: ({settings}: {settings: UserSettings}) => !settingsUtils.canSeeFilePipeline(settings)
    } as ActivityMetricConfig,
    NO_FILES_FOR_PROCESSING: {
        metric: ActivityMetric.NoFilesForProcessing,
        color: ActivityMetricColor[ActivityMetric.NoFilesForProcessing],
        ratioColor: ActivityMetricDefaultColor,
        title: STATISTICS.NO_FILES_FOR_PROCESSING.TITLE,
        tooltip: STATISTICS.NO_FILES_FOR_PROCESSING.TOOLTIP,
        valueGetter: (s) => s.answered - s.someFilesUnprocessed - s.allFilesProcessed,
        totalGetter: (s) => s.answered,
        isHidden: ({settings}: {settings: UserSettings}) => !settingsUtils.canSeeFilePipeline(settings)
    } as ActivityMetricConfig,
    CLIENT_DEVICE_BROWSER: {
        metric: ActivityMetric.ClientDeviceBrowser,
        color: ActivityMetricColor[ActivityMetric.ClientDeviceBrowser],
        title: SESSION_T_KEYS.CLIENT_DEVICE.BROWSER,
        tooltip: STATISTICS.CLIENT_DEVICE_BROWSER.TOOLTIP,
        valueGetter: (s) => s.device?.[ClientDevice.Browser] || 0,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
    CLIENT_DEVICE_ANDROID_SDK: {
        metric: ActivityMetric.ClientDeviceAndroidSdk,
        color: ActivityMetricColor[ActivityMetric.ClientDeviceAndroidSdk],
        title: SESSION_T_KEYS.CLIENT_DEVICE.ANDROID_SDK,
        tooltip: STATISTICS.CLIENT_DEVICE_ANDROID_SDK.TOOLTIP,
        valueGetter: (s) => s.device?.[ClientDevice.AndroidSDK] || 0,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
    CLIENT_DEVICE_IOS_SDK: {
        metric: ActivityMetric.ClientDeviceIosSDK,
        color: ActivityMetricColor[ActivityMetric.ClientDeviceIosSDK],
        title: SESSION_T_KEYS.CLIENT_DEVICE.IOS_SDK,
        tooltip: STATISTICS.CLIENT_DEVICE_IOS_SDK.TOOLTIP,
        valueGetter: (s) => s.device?.[ClientDevice.IosSDK] || 0,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
    CLIENT_DEVICE_SIP: {
        metric: ActivityMetric.ClientDeviceSip,
        color: ActivityMetricColor[ActivityMetric.ClientDeviceSip],
        title: SESSION_T_KEYS.CLIENT_DEVICE.SIP,
        tooltip: STATISTICS.CLIENT_DEVICE_SIP.TOOLTIP,
        valueGetter: (s) => s.device?.[ClientDevice.Sip] || 0,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
    CLIENT_DEVICE_PHONE: {
        metric: ActivityMetric.ClientDevicePhone,
        color: ActivityMetricColor[ActivityMetric.ClientDevicePhone],
        title: SESSION_T_KEYS.CLIENT_DEVICE.PHONE,
        tooltip: STATISTICS.CLIENT_DEVICE_PHONE.TOOLTIP,
        valueGetter: (s) => s.device?.[ClientDevice.Phone] || 0,
        totalGetter: (s) => s.total
    } as ActivityMetricConfig,
};

export const COUNTABLE_ACTIVITY_METRICS_CONFIGS: ActivityMetricConfig[] = [
    METRIC_CONFIG.TOTAL,
    METRIC_CONFIG.UNIQUE_VISITORS,
    METRIC_CONFIG.MISSED,
    METRIC_CONFIG.ANSWERED,
    METRIC_CONFIG.SUCCESSFUL,
    METRIC_CONFIG.FAILED,
    METRIC_CONFIG.ALL_FILES_PROCESSED,
    METRIC_CONFIG.SOME_FILES_UNPROCESSED
];

export const DURATION_ACTIVITY_METRICS_CONFIGS: ActivityMetricConfig[] = [
    METRIC_CONFIG.AVG_RESPONSE_TIME,
    METRIC_CONFIG.AVG_DURATION
];

export const AGENT_METRICS_CONFIGS: ActivityMetricConfig[] = [
    {...METRIC_CONFIG.ANSWERED, totalGetter: null},
    METRIC_CONFIG.AVG_DURATION,
    METRIC_CONFIG.SUCCESSFUL,
    METRIC_CONFIG.FAILED,
    METRIC_CONFIG.ALL_FILES_PROCESSED,
    METRIC_CONFIG.SOME_FILES_UNPROCESSED
];

export const ESCALATION_METRICS_CONFIGS: ActivityMetricConfig[] = [
    METRIC_CONFIG.SELF_RESOLVED,
    METRIC_CONFIG.ESCALATION_ANSWERED,
    METRIC_CONFIG.ESCALATION_MISSED
];

export const CLIENT_DEVICE_METRICS_CONFIGS: ActivityMetricConfig[] = [
    METRIC_CONFIG.CLIENT_DEVICE_BROWSER,
    METRIC_CONFIG.CLIENT_DEVICE_ANDROID_SDK,
    METRIC_CONFIG.CLIENT_DEVICE_IOS_SDK,
    METRIC_CONFIG.CLIENT_DEVICE_SIP,
    METRIC_CONFIG.CLIENT_DEVICE_PHONE,
];

export const ACTIVITY_METRICS_CONFIGS = [
    ...COUNTABLE_ACTIVITY_METRICS_CONFIGS,
    ...ESCALATION_METRICS_CONFIGS,
    ...DURATION_ACTIVITY_METRICS_CONFIGS,
    ...CLIENT_DEVICE_METRICS_CONFIGS
];
